export type InspectionTabKey = 'defectImages' | 'vehicleImages' | 'defectFinder';
export type InspectionAssetTabKey = Extract<InspectionTabKey, 'vehicleImages' | 'defectFinder'>;

export const INSPECTION_TABS = [
  { key: 'defectImages' as const, label: 'Defect Images' },
  { key: 'vehicleImages' as const, label: 'Vehicle Images' },
  { key: 'defectFinder' as const, label: 'Defect Finder' },
] as const;

export const DEFAULT_INSPECTION_TAB: InspectionTabKey = 'defectImages';

export const ALL_INSPECTION_TAB_KEYS: InspectionTabKey[] = INSPECTION_TABS.map(tab => tab.key);

export const PAGE_INSPECTION_TABS: InspectionTabKey[] = ['defectImages', 'vehicleImages', 'defectFinder'];
export const DRAWER_INSPECTION_TABS: InspectionTabKey[] = ['defectImages'];

export const isAssetTab = (tab?: string | null): tab is InspectionAssetTabKey =>
  tab === 'vehicleImages' || tab === 'defectFinder';

export const getAssetTabType = (tab: InspectionTabKey): InspectionAssetTabKey =>
  tab === 'defectFinder' ? 'defectFinder' : 'vehicleImages';

export const getDefaultVisibleTabs = (mode: 'page' | 'drawer'): InspectionTabKey[] =>
  mode === 'drawer' ? DRAWER_INSPECTION_TABS : PAGE_INSPECTION_TABS;

export const resolveInspectionTab = ({
  hasDamages,
  activeTab,
  userSelectedTab,
  visibleTabs,
}: {
  hasDamages: boolean;
  activeTab: InspectionTabKey;
  userSelectedTab: boolean;
  visibleTabs: InspectionTabKey[];
}): InspectionTabKey => {
  if (!visibleTabs.includes(activeTab)) {
    return visibleTabs[0] ?? DEFAULT_INSPECTION_TAB;
  }

  // Default to Vehicle Images when there are no damages for this inspection
  if (!hasDamages && activeTab === 'defectImages' && !userSelectedTab && visibleTabs.includes('vehicleImages')) {
    return 'vehicleImages';
  }

  return activeTab;
};

export const shouldShowAnnotationDeleteAction = (activeTab?: string | null, isSavedDamage = false): boolean =>
  (activeTab === 'defectImages' || activeTab === 'vehicleImages') && isSavedDamage;

export const shouldShowAnnotationToolbarSave = (activeTab?: string | null, hasSavedDamage = false): boolean =>
  isAssetTab(activeTab) && !hasSavedDamage;

export const shouldHideAnnotationHistoryPanel = (activeTab?: string | null, hasHistory = false): boolean =>
  activeTab === 'defectFinder' || (activeTab === 'vehicleImages' && !hasHistory);

export const shouldCloseMediaAfterAnnotationCreate = (activeTab?: string | null): boolean => isAssetTab(activeTab);

/** Defect Images = single in-progress annotation; Vehicle Images / Defect Finder = multiple. */
export const shouldEnforceSingleAnnotation = (activeTab?: string | null): boolean => activeTab === 'defectImages';

export const allowsMultipleAnnotations = (activeTab?: string | null): boolean => !shouldEnforceSingleAnnotation(activeTab);

/** Vehicle Images: download the original CDN file. Defect Images: canvas with annotations. */
export const shouldDownloadOriginalImage = (activeTab?: string | null): boolean => activeTab === 'vehicleImages';

